import { useState, ReactNode, useRef, useLayoutEffect } from 'react';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  delay?: number;
  align?: 'center' | 'left';
}

export const Tooltip = ({
  children,
  content,
  delay = 200,
  align = 'center',
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [shouldAlignRight, setShouldAlignRight] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (timeoutId) clearTimeout(timeoutId);
    const id = window.setTimeout(() => setIsVisible(true), delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) clearTimeout(timeoutId);
    const id = window.setTimeout(() => setIsVisible(false), delay);
    setTimeoutId(id);
  };

  useLayoutEffect(() => {
    if (
      isVisible &&
      triggerRef.current &&
      tooltipRef.current &&
      align === 'left'
    ) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      setShouldAlignRight(
        triggerRect.left + tooltipRect.width > viewportWidth - 20
      );
    } else if (!isVisible) {
      setShouldAlignRight(false);
    }
  }, [isVisible, align]);

  const getPositionClasses = () => {
    if (align === 'left') {
      if (shouldAlignRight) {
        return 'bottom-full right-0 mb-2';
      }
      return 'bottom-full left-0 mb-2';
    }
    return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
  };

  const getArrowClasses = () => {
    if (align === 'left') {
      if (shouldAlignRight) {
        return 'right-4';
      }
      return 'left-4';
    }
    return 'left-1/2 transform -translate-x-1/2';
  };

  return (
    <div className="relative inline-block" ref={triggerRef}>
      <div onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 px-2 py-1 text-xs text-gray-900 bg-white border border-red-800 rounded-lg shadow-lg whitespace-nowrap ${getPositionClasses()}`}
        >
          {content}
          <div className={`absolute top-full ${getArrowClasses()} -mt-1`}>
            <div className="border-4 border-transparent border-t-red-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export const TooltipProvider = ({ children }: { children: ReactNode }) => {
  return <>{children}</>;
};

export const TooltipTrigger = ({ children }: { children: ReactNode }) => {
  return <>{children}</>;
};

export const TooltipContent = ({ children }: { children: ReactNode }) => {
  return <>{children}</>;
};
